import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import useHopOnChange from '../../Hooks/useHopOnChange';
import useFitText from '../../Hooks/useFitText';
import arrowForwardIcon from '../../../../Assets/icons/arrow_forward.svg';

/**
 * Label, hopping total, arrow - in the theme's action color. Shape and
 * placement are left to the shell (CartBar, CartPanel) via `styled()`.
 */
const Button = styled.button`
  appearance: none;
  border: none;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  /* Boxes hug the glyphs, so centering stays true at every fitted size. */
  line-height: 1;
  font-family: var(--eposFontFamily);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.2px;
  background: var(--eposButton);
  color: var(--eposButtonText);
  transition: filter 0.15s ease-out;

  &.is-touching {
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/**
 * Label and amounts together: the part that must fit between the padding and
 * the arrow. Everything inside sizes in `em`, so useFitText can shrink the
 * whole group by setting one font-size here - "1 000 kr" never runs under the arrow.
 * Label pinned left, amounts right, so a changing sum never moves the label.
 */
const Content = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.57em;
  overflow: hidden;
`;

const Label = styled.span`
  flex: 0 0 auto;
`;

/** Original struck through, then what is actually paid - baseline aligned. */
const Amounts = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: baseline;
  gap: 0.3em;
  line-height: 1;
`;

/** What it would have cost before discounts - only when it differs. */
const Original = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: 0.7em;
  font-weight: 600;
  opacity: 0.6;
  text-decoration: line-through;
`;

/** The order total between the label and the arrow - the one sum the shopper sees, hops when it changes. */
const Total = styled.span`
  display: inline-block;
  /* Every digit the same width - the sum only grows when a digit is added. */
  font-variant-numeric: tabular-nums;
  font-size: 0.93em;
  font-weight: 800;
  will-change: transform;
`;

/** Sized by the shell through the `epos-checkout-arrow` class. */
const Arrow = styled.span`
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: inline-flex;
`;

interface Props {
  /** Formatted order total after discounts. */
  total: string;
  /** Formatted total before discounts - struck through when it differs from `total`. */
  originalTotal?: string;
  onCheckout: () => void;
  /** Nothing to pay for (empty cart or zero total) - the button is shown but inert. */
  disabled?: boolean;
  className?: string;
}

/** The one "Continue to payment" button - every cart shell renders this. */
const CheckoutButton: FC<Props> = ({
  total,
  originalTotal,
  onCheckout,
  disabled = false,
  className,
}) => {
  const { i18n } = useI18n();
  const totalRef = useHopOnChange<HTMLSpanElement>(total);
  /** Re-fits whenever either amount changes - both are in the measured group. */
  const contentRef = useFitText<HTMLSpanElement>(`${total}|${originalTotal ?? ''}`);

  const { touching, disabledToast, ...button } = useButton({ onClick: onCheckout, disabled });

  const classes = [className, touching && 'is-touching'].filter(Boolean).join(' ');

  return (
    <Button {...button} className={classes || undefined} data-testid="epos-checkout-button">
      <Content ref={contentRef} className="epos-checkout-content">
        <Label>{i18n('Express.Checkout')}</Label>
        <Amounts className="epos-checkout-amounts">
          {!!originalTotal && originalTotal !== total && (
            <Original data-testid="epos-checkout-original-total">{originalTotal}</Original>
          )}
          <Total ref={totalRef} className="epos-checkout-total" data-testid="epos-checkout-total">
            {total}
          </Total>
        </Amounts>
      </Content>
      {/* Arrow last - it points where the shopper is going. */}
      <Arrow className="epos-checkout-arrow">
        <Icon src={arrowForwardIcon} color="var(--eposButtonText)" width="100%" height="100%" />
      </Arrow>
    </Button>
  );
};

export default memo(CheckoutButton);
